/* eslint-disable max-lines */
import React, { createContext, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import get from 'lodash/get'
import * as Sentry from '@sentry/react'

import { toast } from '@fielded/shared-ui/src/components/Page'
import { SHIPMENT_GPS_FAIL_REASONS } from '@fielded/fs-api/lib/shipment/constants'

import { isWithinDeliveryRadius, SHIPMENT_DELIVERY_DISCREPANCY_FLAGS } from '../../utils'
import { PERMISSION_STATES, PERMISSION_ERRORS } from '../../../../../../../utils/permissions-service'
import { getGeolocation } from '../../../../../../../utils/geolocation'
import PSMDeliveryAuthentication from './PSMDeliveryAuthentication'

export const AuthenticationContext = createContext()

const NUMBER_OF_DIGITS = 4
const DIRECTIONS = {
  LEFT: 'left',
  RIGHT: 'right'
}

const AUTH_TYPES = {
  location: { title: 'Location confirmation', type: 'location', checkboxText: 'Unable to verify your location?', buttonText: 'Verify my location', alternateButtonText: 'Proceed' },
  otp: { title: 'OTP confirmation', type: 'otp', checkboxText: 'Unable to verify OTP?', buttonText: 'Submit', alternateButtonText: 'Submit' }
}

const DEFAULT_OTP_INPUTS = new Array(NUMBER_OF_DIGITS).fill('')

const PSMDeliveryAuthenticationContainer = ({
  api,
  otpKey,
  skipGPSValidation,
  skipOTPValidation,
  isBottomDrawerOpen,
  history,
  shipmentId,
  snapshotId,
  permission,
  geoPosition,
  position,
  distance,
  facilityName,
  locationObject,
  positionErrorMsg,
  hasValidFacilityCoordinates,
  onClose,
  onRetryGeolocation,
  isRetryingGps,
  trackingHandler
}) => {
  const isNoFacilityCoords = !hasValidFacilityCoordinates
  const [locationAuthType, otpAuthType] = Object.keys(AUTH_TYPES)

  const dispatch = useDispatch()

  const [otp, setOtp] = useState(DEFAULT_OTP_INPUTS)
  const [authType, setAuthType] = useState(skipGPSValidation && !skipOTPValidation ? otpAuthType : locationAuthType)
  const [direction, setDirection] = useState(DIRECTIONS.LEFT)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [withAlternateText, setWithAlternateText] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [hasGpsTimeout, setHasGpsTimeout] = useState(false)
  const [authOptions, setAuthOptions] = useState({
    location: { isAuthenticated: false, isUnverified: false, hasError: false, comment: '' },
    otp: { isAuthenticated: false, isUnverified: false, hasError: false, comment: '' }
  })

  const GPS_UNAVAILABLE_MESSAGE = 'We could not capture your GPS location. Tap "Retry GPS" to grant or refresh location access.'

  const showGpsErrorToast = (message) => {
    toast({
      title: message,
      type: 'danger',
      autoDismiss: false,
      ctaProps: {
        label: 'Retry GPS',
        onClick: onRetryGeolocation
      }
    })
  }

  // GPS accuracy is captured and persisted on the verification snapshot
  // for later analysis only - it does not gate the radius check today.
  // Accuracy-aware UX will be designed with UI/UX in a follow-up ticket.
  const accuracy = get(geoPosition, 'coords.accuracy')
  const isWithinLocationRadius = isWithinDeliveryRadius(distance)

  const facilityLatLng = get(locationObject, 'additionalData.latLng')
  const hasNoCoordinates = !facilityLatLng || facilityLatLng === 'null,null'

  const authTypeInfo = AUTH_TYPES[authType]
  const { location: locationOption, otp: otpOption } = authOptions

  const isLocationAuthType = authType === locationAuthType
  const isOTPAuthType = authType === otpAuthType

  const isDeniedPermission = permission === PERMISSION_STATES.DENIED || positionErrorMsg === PERMISSION_ERRORS.PERMISSION_DENIED

  const onSetTimeout = (direction = DIRECTIONS.RIGHT, nextAuthType = otpAuthType) => {
    setAuthType(nextAuthType)
    setTimeout(() => {
      setDirection(direction)
    }, 600)
  }

  const onSetAuthOptions = (prop, value) => {
    setAuthOptions(prevOptions => {
      return {
        ...prevOptions,
        [authType]: {
          ...prevOptions[authType],
          [prop]: value
        }
      }
    })
  }

  const onToggleView = (deniedGPSPermissionComment = '') => {
    setIsSaving(true)
    onSaveFailedValidationComment(deniedGPSPermissionComment)
  }

  const onHandleFailedValidationLogEvent = (fieldFlagged) => {
    trackingHandler.shipment.flagged({ shipmentId, fieldFlagged })
  }

  const onSaveGPSValidationFailedComment = async (deniedGPSPermissionComment) => {
    // Hard gate (#23488): never persist a fail-comment with null/null
    // coords. The whole point of the snapshot is to carry where the
    // driver actually was; a `position: { lat: null, lng: null }` record
    // is worse than no record. Close the comment modal so the inline
    // error banner (with Retry GPS) is visible - otherwise the modal
    // covers the feedback and the click looks like a no-op.
    if (!position || position.lat == null || position.lng == null) {
      console.warn('[GPS] Refusing to save fail-comment without a captured position')
      onSetAuthOptions('hasError', true)
      setIsSaving(false)
      onCloseModal(true)
      showGpsErrorToast(GPS_UNAVAILABLE_MESSAGE)
      return
    }

    const gpsValidationFailedComment = (isDeniedPermission || isNoFacilityCoords) ? deniedGPSPermissionComment : locationOption.comment
    Sentry.withScope(scope => {
      scope.setLevel('warning')
      scope.setTag('component', 'PSMDeliveryAuthentication')
      scope.setExtra('reason', gpsValidationFailedComment)
      scope.setExtra('distanceKm', distance)
      scope.setExtra('accuracy', accuracy != null ? `${Math.round(accuracy)}m` : 'unknown')
      scope.setExtra('driverPosition', position)
      Sentry.captureMessage('Driver bypassed GPS location verification')
    })
    try {
      await api.shipment.addGpsValidationFailComment(snapshotId, gpsValidationFailedComment, position, accuracy)
      onHandleFailedValidationLogEvent(SHIPMENT_DELIVERY_DISCREPANCY_FLAGS.LOCATION_VERIFICATION)

      if (skipOTPValidation) {
        history.push(`/shipments/pick-list/${snapshotId}`)
      } else {
        onSetTimeout()
        onCloseModal(true)
      }
    } catch (error) {
      console.warn(error)
      onSetAuthOptions('hasError', true)
      const apiMessage = (error && error.message) ? error.message : 'We could not save the verification snapshot. Please retry GPS or try again.'
      // Close the reason modal first so the toast is not hidden behind it.
      onCloseModal(true)
      showGpsErrorToast(apiMessage)
    } finally {
      setIsSaving(false)
    }
  }

  const onSaveOTPValidationFailedComment = async () => {
    await api.shipment.otp.couch.createFailSnapshot(snapshotId, otpOption.comment)
    onHandleFailedValidationLogEvent(SHIPMENT_DELIVERY_DISCREPANCY_FLAGS.OTP_VALIDATION)

    setIsSaving(false)
    history.push(`/shipments/pick-list/${snapshotId}`)
  }

  const onSaveFailedValidationComment = async (deniedGPSPermissionComment) => {
    try {
      if (isLocationAuthType && (locationOption.comment || deniedGPSPermissionComment)) {
        return onSaveGPSValidationFailedComment(deniedGPSPermissionComment)
      }

      if (isOTPAuthType && otpOption.comment) {
        return onSaveOTPValidationFailedComment()
      }
    } catch (error) {
      console.warn(error)
    }
  }

  const onAuthenticateLocation = async () => {
    // Hard gate: the verification snapshot must carry the driver's lat/long
    // (see #23488). If we somehow reach this handler without a captured
    // position, surface a clear error and let the driver retry GPS rather
    // than firing a request the backend will reject with 400.
    if (!position || position.lat == null || position.lng == null) {
      onSetAuthOptions('hasError', true)
      showGpsErrorToast(GPS_UNAVAILABLE_MESSAGE)
      return
    }

    setIsVerifying(true)

    try {
      const [fLat, fLng] = get(locationObject, 'additionalData.latLng', '').split(',').map(parseFloat)
      const knownDestCoords = (!isNaN(fLat) && !isNaN(fLng)) ? { lat: fLat, long: fLng } : null
      const response = await api.shipment.gpsValidation(shipmentId, { lat: position.lat, long: position.lng }, knownDestCoords, accuracy)

      if (response && response.comment) {
        onSetAuthOptions('isAuthenticated', true)
        if (skipOTPValidation) {
          history.push(`/shipments/pick-list/${snapshotId}`)
        } else {
          onSetTimeout()
        }
      }
    } catch (error) {
      console.warn(error)
      onSetAuthOptions('hasError', true)
      const apiMessage = (error && error.message) ? error.message : 'GPS verification could not be completed. Please retry GPS, or record a reason if location is unavailable.'
      showGpsErrorToast(apiMessage)
    } finally {
      setIsVerifying(false)
    }
  }

  const onAuthenticateOTP = async () => {
    setIsVerifying(true)

    const otpPassword = otp.join('')
    const otpConfig = api.shipment.otp.getConfig(otpKey.version)

    if (otpPassword.length !== otpConfig.length) {
      onSetAuthOptions('hasError', true)
      setIsVerifying(false)
      return
    }

    try {
      // verify OTP
      const verified = await api.shipment.otp.verify(
        window.crypto, otpPassword, otpKey
      )

      if (!verified) {
        onSetAuthOptions('hasError', true)
        return
      }

      // Store password in shipment snapshot
      await api.shipment.otp.couch.createPasswordSnapshot(snapshotId, otpPassword)
      onSetAuthOptions('isAuthenticated', verified)

      history.push(`/shipments/pick-list/${snapshotId}`)
    } catch (error) {
      console.warn(error)
      onSetAuthOptions('hasError', true)
    } finally {
      setIsVerifying(false)
    }
  }

  const onCoordinatesMissingProceed = async () => {
    setIsVerifying(true)
    try {
      // Force one-shot GPS fix - permission prompt closes before watchPosition delivers a position.
      let capturedPosition = position
      let capturedAccuracy = accuracy
      const hasPosition = capturedPosition && capturedPosition.lat != null && capturedPosition.lng != null
      if (!hasPosition && !isDeniedPermission) {
        try {
          const result = await getGeolocation(PERMISSION_STATES.PROMPT, dispatch, { maximumAge: 5000, timeout: 10000 })
          capturedPosition = result.position
          capturedAccuracy = get(result, 'geoPosition.coords.accuracy')
        } catch (error) {
          console.warn(error)
        }
      }

      // Hard gate (#23488): if even the one-shot fix failed to produce a
      // position, don't write a useless null/null snapshot. The driver
      // must fix GPS/permission and retry.
      if (!capturedPosition || capturedPosition.lat == null || capturedPosition.lng == null) {
        onSetAuthOptions('hasError', true)
        showGpsErrorToast(GPS_UNAVAILABLE_MESSAGE)
        return
      }

      await api.shipment.addGpsValidationFailComment(
        snapshotId,
        SHIPMENT_GPS_FAIL_REASONS.location_coordinates_missing,
        capturedPosition,
        capturedAccuracy
      )
      onHandleFailedValidationLogEvent(SHIPMENT_DELIVERY_DISCREPANCY_FLAGS.LOCATION_VERIFICATION)

      if (skipOTPValidation) {
        history.push(`/shipments/pick-list/${snapshotId}`)
      } else {
        onSetTimeout()
      }
    } catch (error) {
      console.warn(error)
      onSetAuthOptions('hasError', true)
      const apiMessage = (error && error.message) ? error.message : 'We could not save the verification snapshot. Please retry GPS or try again.'
      showGpsErrorToast(apiMessage)
    } finally {
      setIsVerifying(false)
    }
  }

  const onNoFacilityCoordsComment = () => {
    const comment = 'Facility has no location coordinates on record'
    onToggleView(comment)
  }

  const onAuthenticate = (type) => {
    if (isLocationAuthType) {
      // Denied permission: no bypass. The driver must reset the browser
      // permission and retry; the verify CTA is disabled in this state
      // so reaching this handler is a safety net.
      if (isDeniedPermission) {
        onSetAuthOptions('hasError', true)
        showGpsErrorToast(GPS_UNAVAILABLE_MESSAGE)
        return
      }
      if (hasNoCoordinates) {
        return onCoordinatesMissingProceed()
      }
      if (locationOption.isUnverified || !isWithinLocationRadius) {
        if (isNoFacilityCoords) {
          return onNoFacilityCoordsComment()
        }
        return onOpenModal()
      }
      onAuthenticateLocation()
    }

    if (isOTPAuthType) {
      if (otpOption.isUnverified) {
        return onOpenModal()
      }
      onAuthenticateOTP()
    }
  }

  const onOpenModal = () => {
    setIsModalOpen(true)
  }

  const onCloseModal = (preserveComment = false) => {
    if (isLocationAuthType && !preserveComment) {
      onSetAuthOptions('comment', '')
    }

    if (isOTPAuthType) {
      onSetAuthOptions('comment', '')
    }
    setIsModalOpen(false)
  }

  const onHandleOTPChange = (value, index) => {
    if (otpOption.hasError) {
      onSetAuthOptions('hasError', false)
    }

    const newOtp = [...otp]
    newOtp[index] = value
    setOtp(newOtp)
  }

  const onHandleCheckboxToggle = (event) => {
    const isChecked = event.target.checked
    onSetAuthOptions('isUnverified', isChecked)

    if (isChecked) {
      if (isLocationAuthType) {
        setWithAlternateText(true)
      }

      if (isOTPAuthType) {
        setOtp(DEFAULT_OTP_INPUTS)
      }
    } else {
      setWithAlternateText(false)
    }
  }

  const onHandleCommentChange = (event) => {
    const comment = event
    onSetAuthOptions('comment', comment)
  }

  useEffect(() => {
    if ((isLocationAuthType && isDeniedPermission)) {
      onSetAuthOptions('hasError', true)
    }
  }, [isLocationAuthType, isDeniedPermission])

  const hasPosition = position && position.lat != null && position.lng != null
  const isLocating = isLocationAuthType && !isDeniedPermission && !isNoFacilityCoords && !hasPosition

  // GPS acquisition lifecycle: while we still need a fix, escalate gracefully
  // - 15s: mark timeout (flips the in-drawer CTA to Retry GPS) and auto-retry once
  // - 30s: surface the Retry GPS toast as the explicit next action
  // The cleanup runs as soon as a position arrives (isLocating flips false),
  // cancelling any pending timers - so a successful fix silently wins.
  useEffect(() => {
    if (!isLocating) {
      setHasGpsTimeout(false)
      return
    }
    const retryTimer = setTimeout(() => {
      setHasGpsTimeout(true)
      onRetryGeolocation()
    }, 15000)
    const toastTimer = setTimeout(() => {
      showGpsErrorToast(GPS_UNAVAILABLE_MESSAGE)
    }, 30000)
    return () => {
      clearTimeout(retryTimer)
      clearTimeout(toastTimer)
    }
  }, [isLocating])

  const values = {
    isBottomDrawerOpen,
    history,
    permission,
    geoPosition,
    position,
    locationObject,
    positionErrorMsg,
    otp,
    authType,
    authTypeInfo,
    isLocationAuthType,
    isOTPAuthType,
    authOptions,
    direction,
    distance,
    facilityName,
    withAlternateText,
    isModalOpen,
    isDeniedPermission,
    isNoFacilityCoords,
    isVerifying,
    isSaving,
    isWithinLocationRadius,
    hasNoCoordinates,
    isRetryingGps,
    isLocating,
    hasGpsTimeout,
    onClose,
    onCloseModal,
    onToggleView,
    onHandleOTPChange,
    onHandleCheckboxToggle,
    onHandleCommentChange,
    onAuthenticate,
    onRetryGeolocation
  }

  return (
    <AuthenticationContext.Provider value={values}>
      <PSMDeliveryAuthentication />
    </AuthenticationContext.Provider>
  )
}

export default PSMDeliveryAuthenticationContainer
